/* AOP Budget — Details tab table: contract/opportunity rows grouped into
   section header rows by business type (order/count comes straight from the
   API — see APIIntegrationFiles/AOP_BUDGET_API.md's "Business type count is
   not fixed" note), each section followed by its own Subtotal row, with a
   grand-total row after every section. Sortable click-to-cycle column
   headers and filter-icon popovers are the same system Waterfall EAC uses.
   Pure presentation — all filter/sort STATE lives in Details.js; this file
   only renders what it's handed. */
import React from "react";
import { createPortal } from "react-dom";
import {
  WaterfallThLabel,
  cellWhiteSpaceStyle,
  dynamicColSize,
} from "../waterfall-eac/waterfallTableUtils";
import { fpBase, TextFilterContent, NumericFilterContent, OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";
import { TABLE_FONT, TH_BG, TH_TEXT, TH_BORDER, CELL_FG, TH_BG_BAND } from "../../reportTableConfig";
import { fmtMoney, fmtDate } from "./aopBudgetUtils";

const FONT = TABLE_FONT;
const NEG = "#D64550";

function buildCols(nextYear) {
  return [
    // `flex: true` — the one column with no natural cap (contract/opportunity
    // names vary widely in length) — absorbs whatever width the other,
    // fixed-width columns don't need, so the table fills the page instead of
    // stopping at the fixed columns' summed width with blank space after it.
    { key: "name", label: "Name", w: 280, align: "left", filter: "text", flex: true },
    { key: "id", label: "Contract ID", w: 130, align: "left", filter: "text" },
    { key: "revenue", label: nextYear ? `${nextYear} Revenue` : "Revenue", w: 140, align: "right", filter: "numeric" },
    { key: "revenueStartMonth", label: "Revenue Start Month", w: 150, align: "left" },
    { key: "revenueThruMonth", label: "Revenue Thru Month", w: 150, align: "left" },
    { key: "popStartDate", label: "POP Start Date", w: 130, align: "right" },
    { key: "popEndDate", label: "POP End Date", w: 130, align: "right" },
  ];
}

/** Fixed columns get an exact width/minWidth/maxWidth (never grow or
 *  shrink); the flex column gets only a minWidth, so it's the one that
 *  absorbs any extra table width. */
function colSizeStyle(col) {
  return col.flex ? { minWidth: col.w } : dynamicColSize(col.w);
}
const thStyle = (col) => ({
  position: "sticky",
  top: 0,
  background: TH_BG,
  color: TH_TEXT,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "10px 10px",
  textAlign: col.align,
  border: "none",
  boxShadow: `inset 0 -1px 0 ${TH_BORDER}`,
  ...colSizeStyle(col),
});
const tdStyle = (col, alt) => ({
  padding: "8px 10px",
  fontSize: 12.5,
  color: CELL_FG,
  textAlign: col.align,
  background: alt ? "#F8FAFC" : "#fff",
  borderBottom: "1px solid #E2E8F0",
  ...cellWhiteSpaceStyle(col.align === "right"),
  ...colSizeStyle(col),
});
const sectionRowStyle = {
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "8px 10px",
};
const subtotalCellStyle = (col) => ({
  padding: "8px 10px",
  fontSize: 12.5,
  fontWeight: 700,
  fontStyle: "italic",
  textAlign: col.align,
  color: "#374151",
  background: "#F8FAFC",
  borderBottom: `2px solid ${TH_BORDER}`,
  ...cellWhiteSpaceStyle(col.align === "right"),
  ...colSizeStyle(col),
});
const fBtnSt = (a) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  padding: 0,
  borderRadius: 3,
  border: "none",
  cursor: "pointer",
  background: a ? "rgba(165,180,252,0.18)" : "transparent",
  color: a ? "#A5B4FC" : TH_TEXT,
  opacity: a ? 1 : 0.5,
  flexShrink: 0,
});

const pagBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 16px",
  borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA",
  gap: 12,
  flexWrap: "wrap",
};
const pagTxt = { fontSize: 12, color: "#6B7280", fontFamily: FONT, whiteSpace: "nowrap" };
const pSzSel = { height: 28, border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12, color: "#374151", padding: "0 6px", background: "#fff", cursor: "pointer", fontFamily: FONT };
const pBtn = (d) => ({
  minWidth: 30, height: 28, padding: "0 6px", border: "1px solid #E2E8F0", borderRadius: 6,
  background: "#fff", color: d ? "#94A3B8" : "#374151", fontSize: 12,
  cursor: d ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontFamily: FONT, opacity: d ? 0.35 : 1,
});
const pBtnA = {
  minWidth: 30, height: 28, padding: "0 6px", border: `1px solid ${TH_BG_BAND}`, borderRadius: 6,
  background: TH_BG_BAND, color: "#fff", fontSize: 12, fontWeight: 600,
  cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: FONT,
};
const pDots = { fontSize: 12, color: "#6B7280", padding: "0 4px", userSelect: "none" };
const PAGE_SIZE_OPTIONS = [25, 50, 100];

function PaginationBar({ page, pageSize, totalPages, totalRows, rangeStart, rangeEnd, onPageChange, onPageSizeChange }) {
  if (totalRows === 0) return null;
  const pageNums = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const s = new Set([1, totalPages, page, page - 1, page + 1, page - 2, page + 2]);
    return [...s].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  })();

  return (
    <div style={pagBar}>
      <span style={pagTxt}>{`${rangeStart}–${rangeEnd} of ${totalRows} rows`}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={pagTxt}>Per page</span>
        <select style={pSzSel} value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
          {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <button style={pBtn(page === 1)} disabled={page === 1} onClick={() => onPageChange(1)}>«</button>
          <button style={pBtn(page === 1)} disabled={page === 1} onClick={() => onPageChange(page - 1)}>‹</button>
          {pageNums.map((p, i) => (
            <React.Fragment key={p}>
              {i > 0 && pageNums[i - 1] < p - 1 && <span style={pDots}>…</span>}
              <button style={p === page ? pBtnA : pBtn(false)} onClick={() => onPageChange(p)}>{p}</button>
            </React.Fragment>
          ))}
          <button style={pBtn(page === totalPages)} disabled={page === totalPages} onClick={() => onPageChange(page + 1)}>›</button>
          <button style={pBtn(page === totalPages)} disabled={page === totalPages} onClick={() => onPageChange(totalPages)}>»</button>
        </div>
      </div>
    </div>
  );
}

function FilterIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 4h18l-7 8.5V20l-4-2v-5.5L3 4z" />
    </svg>
  );
}

export default function DetailsTable({
  sections,
  grandTotal,
  nextYear,
  sort,
  filterState,
  pagination,
}) {
  const COLS = buildCols(nextYear);
  const { sortKeys, onThClick, getSortLabel } = sort;
  const {
    filterText, hasFilterValue, openFilterKey, toggleFilter, filterBtnRefs,
    filterPos, colFilters, openOpKey, toggleOpDropdown, opBtnRefs, opPos,
    setColFilters, setFilterText, setOpenOpKey,
  } = filterState;

  function renderFilterContent(key) {
    if (key === "name" || key === "id") {
      return (
        <TextFilterContent
          label={COLS.find((c) => c.key === key)?.label}
          value={filterText[key]}
          onChange={(v) => setFilterText((p) => ({ ...p, [key]: v }))}
          onClear={() => setFilterText((p) => ({ ...p, [key]: "" }))}
        />
      );
    }
    const f = colFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={COLS.find((c) => c.key === key)?.label}
        value={f.value}
        op={f.op}
        onValueChange={(v) => setColFilters((p) => ({ ...p, [key]: { ...f, value: v } }))}
        onClear={() => setColFilters((p) => { const n = { ...p }; delete n[key]; return n; })}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  }

  const tableW = COLS.reduce((s, c) => s + c.w, 0);

  return (
    <div style={{ borderRadius: 12, border: "1px solid #EBEBEB", background: "#fff", overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: tableW, fontFamily: FONT }}>
          <thead>
            <tr>
              {COLS.map((c) => {
                const active = openFilterKey === c.key || hasFilterValue(c.key);
                return (
                  <th key={c.key} style={{ ...thStyle(c), cursor: "pointer" }} onClick={onThClick(c.key)}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: c.align === "right" ? "flex-end" : "space-between", gap: 4 }}>
                      <WaterfallThLabel label={c.label} colKey={c.key} sortKeys={sortKeys} getLabel={getSortLabel} align={c.align} textColor={TH_TEXT} highlight={openFilterKey === c.key} />
                      {c.filter && (
                        <button
                          ref={(el) => { filterBtnRefs.current[c.key] = el; }}
                          data-filter-btn
                          style={fBtnSt(active)}
                          onClick={(e) => { e.stopPropagation(); toggleFilter(c.key, e.currentTarget); }}
                        >
                          <FilterIcon />
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sections.length === 0 && (
              <tr>
                <td colSpan={COLS.length} style={{ padding: 32, textAlign: "center", color: "#94A3B8", fontSize: 12.5 }}>
                  No rows match the current filters.
                </td>
              </tr>
            )}
            {sections.map((s) => (
              <React.Fragment key={s.businessType}>
                <tr>
                  <td colSpan={COLS.length} style={sectionRowStyle}>{s.businessType}</td>
                </tr>
                {s.rows.map((r, i) => (
                  <tr key={`${s.businessType}-${r.id ?? i}`}>
                    {COLS.map((c) => (
                      <td key={c.key} style={{ ...tdStyle(c, i % 2 === 1), color: c.key === "revenue" && Number(r[c.key]) < 0 ? NEG : undefined }}>
                        {c.key === "revenue" ? fmtMoney(r[c.key]) : c.key === "popStartDate" || c.key === "popEndDate" ? fmtDate(r[c.key]) : (r[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
                {s.showSubtotal !== false && (
                  <tr>
                    <td style={subtotalCellStyle(COLS[0])}>Subtotal</td>
                    <td style={subtotalCellStyle(COLS[1])} />
                    <td style={{ ...subtotalCellStyle(COLS[2]), color: Number(s.subtotalRevenue) < 0 ? NEG : undefined }}>
                      {fmtMoney(s.subtotalRevenue)}
                    </td>
                    {COLS.slice(3).map((c) => (
                      <td key={c.key} style={subtotalCellStyle(c)} />
                    ))}
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {sections.length > 0 && (
            <tfoot>
              <tr>
                <td style={{ ...tdStyle(COLS[0], false), fontWeight: 700, background: "#F0FFF4" }}>Total</td>
                <td style={{ ...tdStyle(COLS[1], false), background: "#F0FFF4" }} />
                <td style={{ ...tdStyle(COLS[2], false), fontWeight: 700, color: Number(grandTotal) < 0 ? NEG : undefined, background: "#F0FFF4" }}>
                  {fmtMoney(grandTotal)}
                </td>
                {COLS.slice(3).map((c) => (
                  <td key={c.key} style={{ ...tdStyle(c, false), background: "#F0FFF4" }} />
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {pagination && <PaginationBar {...pagination} />}

      {openFilterKey &&
        createPortal(
          <div data-filter-panel style={{ ...fpBase, top: filterPos.top, left: filterPos.left }}>
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey] || {}).op || "gt"}
            onSelect={(op) => {
              setColFilters((p) => ({ ...p, [openOpKey]: { ...(p[openOpKey] || { value: "" }), op } }));
              setOpenOpKey(null);
            }}
            style={{ top: opPos.top, left: opPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}
